/**
 * Die drei Bearbeitungsmodi einer Serie.
 *
 * Das ist ein Datenmodell-Problem, kein Oberflaechenproblem: jeder Modus
 * schreibt an andere Stellen. Deshalb liefern diese Funktionen einen
 * *Schreibplan* statt selbst zu schreiben - sie sind damit vollstaendig ohne
 * Datenbank testbar, und der Aufrufer fuehrt den Plan in EINER Transaktion aus.
 *
 *   "Nur dieser Termin"          -> EXDATE + Ausnahmezeile
 *   "Dieser und alle folgenden"  -> Serie abschneiden + neue Serie
 *   "Alle Termine"               -> Serie direkt aendern
 */
import { RRule, rrulestr } from 'rrule';

import type { CalendarEvent, ID, LocalDateTime } from '@/domain/types';
import { addDays, datePart, minutesOfDay, atMinutes } from '@/lib/datetime';
import { fromFloatingUtc, toFloatingUtc } from '@/lib/recurrence/expand';

/** Felder, die eine Bearbeitung veraendern darf. */
export type EventPatch = Partial<
  Pick<CalendarEvent,
    | 'title' | 'notes' | 'location' | 'areaId' | 'allDay' | 'start' | 'end' | 'rrule'
    | 'categoryId' | 'priority' | 'markFill' | 'markBorder' | 'markDot' | 'projectId' | 'taskIds' | 'reminderMinutes'>
>;

/**
 * Was geschrieben werden soll. Der Aufrufer wendet das in einer Transaktion an.
 * `puts` sind vollstaendige Zeilen (put, nicht update).
 */
export interface WritePlan {
  puts: CalendarEvent[];
  deletes: ID[];
}

const emptyPlan = (): WritePlan => ({ puts: [], deletes: [] });

function touch(event: CalendarEvent, now: string): CalendarEvent {
  return { ...event, updatedAt: now, isRecurring: event.rrule ? 1 : 0 };
}

// ---------------------------------------------------------------------------
// UNTIL
// ---------------------------------------------------------------------------

/**
 * Setzt bzw. ersetzt `UNTIL` in einer Regel.
 *
 * `UNTIL` wird nach RFC 5545 als UTC-Zeitstempel geschrieben. Weil wir in
 * floating space rechnen, ist der Wert hier bewusst die Wanduhrzeit mit einem
 * angehaengten `Z` - `rrule` bekommt ihn ueber `toFloatingUtc` konsistent
 * zurueck. Gemischt werden duerfen die beiden Welten nie.
 */
export function withUntil(rule: string, until: LocalDateTime): string {
  const stamp = `${until.slice(0, 4)}${until.slice(5, 7)}${until.slice(8, 10)}`
    + `T${until.slice(11, 13)}${until.slice(14, 16)}00Z`;

  const parts = rule
    .replace(/^RRULE:/i, '')
    .split(';')
    .filter((p) => p && !/^UNTIL=/i.test(p) && !/^COUNT=/i.test(p));

  parts.push(`UNTIL=${stamp}`);
  return parts.join(';');
}

// ---------------------------------------------------------------------------
// COUNT
// ---------------------------------------------------------------------------

/** `COUNT` einer Regel, oder `undefined`. */
export function countOf(rule: string | undefined): number | undefined {
  const m = rule?.match(/(?:^|;)COUNT=(\d+)/i);
  return m ? +m[1]! : undefined;
}

function withCount(rule: string, count: number): string {
  return rule
    .replace(/^RRULE:/i, '')
    .split(';')
    .filter((p) => p && !/^COUNT=/i.test(p) && !/^UNTIL=/i.test(p))
    .concat(`COUNT=${count}`)
    .join(';');
}

/**
 * Wie viele Vorkommen erzeugt die Serie VOR diesem Zeitpunkt?
 *
 * Ausgeschlossene Termine (EXDATE) zaehlen nach RFC 5545 mit: `COUNT` zaehlt
 * die erzeugten Vorkommen, nicht die angezeigten.
 */
export function occurrencesBefore(master: CalendarEvent, originalStart: LocalDateTime): number {
  if (!master.rrule) return 0;
  try {
    const parsed = rrulestr(`RRULE:${master.rrule.replace(/^RRULE:/i, '')}`, {
      dtstart: toFloatingUtc(master.start),
    });
    if (!(parsed instanceof RRule)) return 0;
    const grenze = toFloatingUtc(originalStart).getTime();
    return parsed.all((d) => d.getTime() < grenze).length;
  } catch {
    return 0;
  }
}

/**
 * Die Regel der neuen Serie bei "dieser und alle folgenden".
 *
 * Laeuft die Serie "nach n Terminen" aus und hat der Nutzer die Anzahl nicht
 * angefasst, bekommt die neue Serie nur den REST - sonst liefe die Serie
 * insgesamt laenger als eingestellt. Eine bewusst geaenderte Anzahl gilt ab
 * diesem Termin (Roadmap F-03).
 */
function ruleForFutureSeries(ctx: EditContext): string {
  const { master, originalStart, patch } = ctx;
  const rule = patch.rrule ?? master.rrule!;
  const alt = countOf(master.rrule);
  const neu = countOf(rule);
  if (alt === undefined || neu !== alt) return rule;
  return withCount(rule, Math.max(1, alt - occurrencesBefore(master, originalStart)));
}

/** Eine Minute vor dem gegebenen Zeitpunkt - die Grenze beim Abschneiden. */
export function oneMinuteBefore(value: LocalDateTime): LocalDateTime {
  const total = minutesOfDay(value) - 1;
  if (total >= 0) return atMinutes(datePart(value), total);
  return atMinutes(addDays(datePart(value), -1), 1439);
}

/** Gibt es nach dem Abschneiden ueberhaupt noch ein Vorkommen? */
function seriesStillHasOccurrences(event: CalendarEvent, rule: string): boolean {
  try {
    const parsed = rrulestr(`RRULE:${rule}`, { dtstart: toFloatingUtc(event.start) });
    if (!(parsed instanceof RRule)) return false;
    return parsed.all((_, i) => i < 1).length > 0;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Die drei Modi - Bearbeiten
// ---------------------------------------------------------------------------

export interface EditContext {
  master: CalendarEvent;
  /** Der urspruengliche Start des angefassten Vorkommens. */
  originalStart: LocalDateTime;
  /** Bereits vorhandene Ausnahmen dieser Serie. */
  overrides: CalendarEvent[];
  patch: EventPatch;
  now: string;
  newId: () => ID;
}

/**
 * "Nur dieser Termin": Vorkommen ausschliessen und durch eine Ausnahmezeile
 * ersetzen. Existiert bereits eine Ausnahme, wird sie geaendert.
 */
export function editSingleOccurrence(ctx: EditContext): WritePlan {
  const { master, originalStart, overrides, patch, now } = ctx;
  const plan = emptyPlan();

  const existing = overrides.find((o) => o.originalStart === originalStart);

  if (existing) {
    plan.puts.push(touch({ ...existing, ...patch }, now));
  } else {
    const base = occurrenceSnapshot(master, originalStart);
    plan.puts.push(touch({
      ...base,
      ...patch,
      id: ctx.newId(),
      recurringEventId: master.id,
      originalStart,
      rrule: undefined,
      exdates: undefined,
      createdAt: now,
      updatedAt: now,
    }, now));
  }

  const exdates = new Set(master.exdates ?? []);
  if (!exdates.has(originalStart)) {
    exdates.add(originalStart);
    plan.puts.push(touch({ ...master, exdates: [...exdates].sort() }, now));
  }

  return plan;
}

/**
 * "Dieser und alle folgenden": die bestehende Serie endet vor diesem
 * Vorkommen, ab hier laeuft eine neue Serie mit den geaenderten Werten.
 * Spaetere Ausnahmen wandern mit.
 */
export function editThisAndFuture(ctx: EditContext): WritePlan {
  const { master, originalStart, overrides, patch, now } = ctx;
  const plan = emptyPlan();

  if (!master.rrule) {
    // Ohne Regel ist "und alle folgenden" dasselbe wie "alle".
    return editAllOccurrences(ctx);
  }

  const newMasterId = ctx.newId();
  const snapshot = occurrenceSnapshot(master, originalStart);
  const delta = patchDelta(originalStart, patch);

  plan.puts.push(touch({
    ...snapshot,
    ...patch,
    id: newMasterId,
    rrule: ruleForFutureSeries(ctx),
    exdates: shiftExdates(
      (master.exdates ?? []).filter((d) => d >= originalStart),
      delta,
    ),
    recurringEventId: undefined,
    originalStart: undefined,
    createdAt: now,
    updatedAt: now,
  }, now));

  // Die alte Serie endet unmittelbar vor diesem Vorkommen.
  const truncated = withUntil(master.rrule, oneMinuteBefore(originalStart));
  const remaining: CalendarEvent = {
    ...master,
    rrule: truncated,
    exdates: (master.exdates ?? []).filter((d) => d < originalStart),
  };

  if (seriesStillHasOccurrences(master, truncated)) {
    plan.puts.push(touch(remaining, now));
  } else {
    // Nichts mehr uebrig - die alte Serie waere eine leere Karteileiche.
    plan.deletes.push(master.id);
  }

  // Ausnahmen ab diesem Zeitpunkt gehoeren jetzt zur neuen Serie - und zeigen
  // auf deren verschobene Vorkommen, nicht mehr auf die der alten.
  for (const override of overrides) {
    if (override.originalStart && override.originalStart >= originalStart) {
      plan.puts.push(touch({
        ...override,
        recurringEventId: newMasterId,
        originalStart: delta === 0
          ? override.originalStart
          : shiftFloating(override.originalStart, delta),
      }, now));
    }
  }

  return plan;
}

/**
 * "Alle Termine": die Serie selbst aendern.
 *
 * Bestehende Ausnahmen bleiben erhalten - so verhaelt sich auch Google
 * Kalender. Verschiebt der Nutzer die Uhrzeit, verschieben sich alle regulaeren
 * Vorkommen, die einzeln angepassten aber nicht. Die Oberflaeche weist einmal
 * darauf hin.
 */
export function editAllOccurrences(ctx: EditContext): WritePlan {
  const { master, overrides, patch, now } = ctx;
  const plan = emptyPlan();

  const delta = master.rrule ? patchDelta(ctx.originalStart, patch) : 0;
  const shifted = shiftSeriesStart(master, delta);

  plan.puts.push(touch({
    ...master,
    ...patch,
    ...shifted,
    exdates: shiftExdates(master.exdates, delta),
  }, now));

  // Die Ausnahmen behalten ihre eigenen Zeiten, muessen aber weiterhin auf das
  // Vorkommen zeigen, das sie ersetzen - und das liegt jetzt woanders.
  if (delta !== 0) {
    for (const override of overrides) {
      if (!override.originalStart) continue;
      plan.puts.push(touch({
        ...override,
        originalStart: shiftFloating(override.originalStart, delta),
      }, now));
    }
  }

  return plan;
}

function shiftExdates(exdates: LocalDateTime[] | undefined, deltaMs: number) {
  if (!exdates?.length) return exdates;
  if (deltaMs === 0) return exdates;
  return exdates.map((d) => shiftFloating(d, deltaMs)).sort();
}

/**
 * Verschiebt der Nutzer ein Vorkommen einer Serie und waehlt "alle", dann ist
 * gemeint: alle um dieselbe Differenz verschieben - nicht die Serie auf das
 * Datum dieses einen Vorkommens setzen.
 */
function shiftSeriesStart(
  master: CalendarEvent,
  deltaMs: number,
): Partial<CalendarEvent> {
  if (deltaMs === 0) return {};

  const start = shiftFloating(master.start, deltaMs);
  const end = shiftFloating(master.end, deltaMs);

  return master.allDay
    ? { start: datePart(start), end: datePart(end) }
    : { start, end };
}

/** Verschiebt einen floating-Wert um eine Millisekundendifferenz. */
export function shiftFloating(value: string, deltaMs: number): LocalDateTime {
  return fromFloatingUtc(new Date(toFloatingUtc(value).getTime() + deltaMs));
}

/**
 * Um wie viel verschiebt dieser Patch das angefasste Vorkommen?
 *
 * Diese Differenz ist der Dreh- und Angelpunkt: verschiebt sich die Serie, dann
 * zeigen `exdates` und `originalStart` der Ausnahmen auf Zeitpunkte, an denen
 * gar kein Vorkommen mehr liegt - die geblockten Vorkommen tauchen wieder auf
 * und der Termin erscheint doppelt. Deshalb wandern beide IMMER mit.
 */
function patchDelta(originalStart: LocalDateTime, patch: EventPatch): number {
  if (!patch.start) return 0;
  return toFloatingUtc(patch.start).getTime() - toFloatingUtc(originalStart).getTime();
}

// ---------------------------------------------------------------------------
// Loeschen
// ---------------------------------------------------------------------------

export interface DeleteContext {
  master: CalendarEvent;
  originalStart: LocalDateTime;
  overrides: CalendarEvent[];
  now: string;
}

/** "Nur dieser Termin" loeschen: EXDATE setzen, keine Ausnahmezeile anlegen. */
export function deleteSingleOccurrence(ctx: DeleteContext): WritePlan {
  const { master, originalStart, overrides, now } = ctx;
  const plan = emptyPlan();

  const existing = overrides.find((o) => o.originalStart === originalStart);
  if (existing) plan.deletes.push(existing.id);

  const exdates = new Set(master.exdates ?? []);
  exdates.add(originalStart);
  plan.puts.push(touch({ ...master, exdates: [...exdates].sort() }, now));

  return plan;
}

export function deleteThisAndFuture(ctx: DeleteContext): WritePlan {
  const { master, originalStart, overrides, now } = ctx;
  const plan = emptyPlan();

  if (!master.rrule) {
    return deleteAllOccurrences(ctx);
  }

  const truncated = withUntil(master.rrule, oneMinuteBefore(originalStart));
  if (seriesStillHasOccurrences(master, truncated)) {
    plan.puts.push(touch({
      ...master,
      rrule: truncated,
      exdates: (master.exdates ?? []).filter((d) => d < originalStart),
    }, now));
  } else {
    plan.deletes.push(master.id);
  }

  for (const override of overrides) {
    if (override.originalStart && override.originalStart >= originalStart) {
      plan.deletes.push(override.id);
    }
  }

  return plan;
}

/** "Alle Termine" loeschen: Serie und saemtliche Ausnahmen. */
export function deleteAllOccurrences(ctx: DeleteContext): WritePlan {
  return {
    puts: [],
    deletes: [ctx.master.id, ...ctx.overrides.map((o) => o.id)],
  };
}

// ---------------------------------------------------------------------------
// Hilfen
// ---------------------------------------------------------------------------

/**
 * Baut aus Serie und Vorkommensstart eine eigenstaendige Terminzeile - also
 * die Serie, aber auf die Zeiten genau dieses Vorkommens gesetzt.
 */
function occurrenceSnapshot(master: CalendarEvent, originalStart: LocalDateTime): CalendarEvent {
  const durationMs = toFloatingUtc(master.end).getTime() - toFloatingUtc(master.start).getTime();
  const start = originalStart;
  const end = fromFloatingUtc(new Date(toFloatingUtc(start).getTime() + durationMs));

  return {
    ...master,
    start: master.allDay ? datePart(start) : start,
    end: master.allDay ? datePart(end) : end,
  };
}
